import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { apiRequest } from '../config/api';
import {
  Shield, Users, Building2, FileText, Globe,
  MessageSquare, ClipboardList, ChevronDown, ChevronRight,
  Search, ArrowUpRight, Rocket, Pause, Play, X, KeyRound, Copy, Check,
  Target, Zap, AlertTriangle, Archive, LogIn, Clock,
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'prospecting', label: 'Prospecting' },
  { id: 'chat', label: 'Chat' },
  { id: 'usage', label: 'Usage' },
];

// --- Helpers ---

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatRelative(dateStr) {
  if (!dateStr) return '-';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(dateStr);
}

function LoadingRows({ cols = 5, rows = 4 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-b border-gray-50">
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j} className="px-4 py-3">
          <div className="h-3.5 bg-gray-100 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
        </td>
      ))}
    </tr>
  ));
}

// --- Overview Tab ---

function OverviewTab({ stats, activity, loadingStats, loadingActivity }) {
  const metrics = stats ? [
    { label: 'Users', value: stats.users, icon: Users },
    { label: 'Organizations', value: stats.organizations, icon: Building2 },
    { label: 'Forms', value: stats.forms, icon: FileText },
    { label: 'Pages', value: stats.pages, icon: Globe },
    { label: 'Sites', value: stats.sites, icon: Rocket },
    { label: 'Responses', value: stats.form_responses, icon: ClipboardList },
    { label: 'Chats', value: stats.chats, icon: MessageSquare },
    { label: 'Prospecting Lists', value: stats.prospecting_lists, icon: Target },
  ] : [];

  return (
    <div className="space-y-8">
      {/* Metric grid — numbers-first, monochrome */}
      {loadingStats ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 rounded-xl overflow-hidden border border-gray-100">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white p-5">
              <div className="h-7 w-16 bg-gray-100 rounded animate-pulse mb-2" />
              <div className="h-3 w-20 bg-gray-50 rounded animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200/60 rounded-xl overflow-hidden border border-gray-200/60">
          {metrics.map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white p-5 group">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold text-gray-900 tracking-tight font-['Satoshi']">
                  {value?.toLocaleString() ?? '0'}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <Icon className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.5} />
                <span className="text-[13px] text-gray-400">{label}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ActivityPanel
          title="Recent Signups"
          loading={loadingActivity}
          items={activity?.recent_signups || []}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <span className="text-[13px] font-medium text-gray-900 truncate block">{item.full_name || item.username}</span>
                <span className="text-xs text-gray-400 truncate block">{item.email}</span>
              </div>
              <span className="text-xs text-gray-300 whitespace-nowrap ml-3 tabular-nums">{formatRelative(item.created_at)}</span>
            </div>
          )}
        />
        <ActivityPanel
          title="Recent Submissions"
          loading={loadingActivity}
          items={activity?.recent_submissions || []}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-gray-700 truncate min-w-0">{item.form_title || 'Untitled Form'}</span>
              <span className="text-xs text-gray-300 whitespace-nowrap ml-3 tabular-nums">{formatRelative(item.submitted_at)}</span>
            </div>
          )}
        />
        <ActivityPanel
          title="Recent Deployments"
          loading={loadingActivity}
          items={activity?.recent_deployments || []}
          renderItem={(item) => (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <span className="text-[13px] font-medium text-gray-900 truncate block">{item.name}</span>
                {item.subdomain && <span className="text-xs text-gray-400">{item.subdomain}.fasttttt.com</span>}
              </div>
              <span className="text-xs text-gray-300 whitespace-nowrap ml-3 tabular-nums">{formatRelative(item.last_deployed_at)}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function ActivityPanel({ title, items, renderItem, loading }) {
  return (
    <div className="border border-gray-100 rounded-xl bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="text-[13px] font-medium text-gray-900">{title}</span>
        {!loading && <span className="text-xs text-gray-300 tabular-nums">{items.length}</span>}
      </div>
      <div className="divide-y divide-gray-50">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-4 py-3">
              <div className="h-3.5 w-3/4 bg-gray-50 rounded animate-pulse mb-1.5" />
              <div className="h-3 w-1/2 bg-gray-50 rounded animate-pulse" />
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-gray-300">No recent activity</div>
        ) : (
          items.slice(0, 8).map((item, i) => (
            <div key={item.id || i} className="px-4 py-2.5">{renderItem(item)}</div>
          ))
        )}
      </div>
    </div>
  );
}

// --- Users Tab ---

function UsersTab({ users, loading, onToggleSuperadmin, onToggleCooldown, onResetPassword }) {
  const [search, setSearch] = useState('');
  const [confirmReset, setConfirmReset] = useState(null); // { id, email }
  const [resetResult, setResetResult] = useState(null); // { email, password }
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Dismiss modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (resetResult) {
          setResetResult(null);
        } else if (confirmReset && !resetting) {
          setConfirmReset(null);
        }
      }
    };
    if (confirmReset || resetResult) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [confirmReset, resetResult, resetting]);

  const filtered = (users || []).filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleResetConfirmed = async () => {
    if (!confirmReset) return;
    setResetting(true);
    const result = await onResetPassword(confirmReset.id);
    setResetting(false);
    setConfirmReset(null);
    if (result) {
      setResetResult({ email: result.email, password: result.new_password });
      setCopied(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!resetResult?.password) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = resetResult.password;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-colors"
        />
      </div>

      <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Email</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Username</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Status</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Role</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Orgs</th>
              <th className="text-center px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Chat</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Joined</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400" />
            </tr>
          </thead>
          <tbody>
            {loading ? <LoadingRows cols={9} rows={6} /> : filtered.map(user => (
              <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-2.5 text-gray-900 font-medium">{user.full_name || '-'}</td>
                <td className="px-4 py-2.5 text-gray-500">{user.email}</td>
                <td className="px-4 py-2.5 text-gray-500">{user.username}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ${
                    user.is_active ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  {user.is_superadmin ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-900 text-white rounded-md">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">Member</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-center text-gray-500 tabular-nums">{user.organization_count}</td>
                <td className="px-4 py-2.5 text-center">
                  <button
                    onClick={() => onToggleCooldown(user.id, !user.chat_cooldown)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md transition-colors ${
                      user.chat_cooldown
                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                        : 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                    }`}
                    title={user.chat_cooldown ? 'Click to lift cooldown' : 'Click to enable cooldown'}
                  >
                    {user.chat_cooldown ? <><Pause className="w-3 h-3" /> Cooldown</> : <><Play className="w-3 h-3" /> Active</>}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-gray-400 text-xs tabular-nums">{formatDate(user.created_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => setConfirmReset({ id: user.id, email: user.email })}
                      className="text-xs px-2.5 py-1 rounded-lg font-medium transition-colors text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                      title="Reset password"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onToggleSuperadmin(user.id, !user.is_superadmin)}
                      className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                        user.is_superadmin
                          ? 'text-gray-500 hover:text-red-600 hover:bg-red-50'
                          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {user.is_superadmin ? 'Revoke' : 'Grant Admin'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-[13px] text-gray-300">No users found</div>
        )}
      </div>

      {/* Confirm Reset Password Modal */}
      {confirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !resetting && setConfirmReset(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <KeyRound className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Reset Password</h3>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              This will generate a new random password for <strong>{confirmReset.email}</strong>.
              Their current password will stop working immediately.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmReset(null)}
                disabled={resetting}
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetConfirmed}
                disabled={resetting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Result Modal */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setResetResult(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Password Reset</h3>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              New password for <strong>{resetResult.email}</strong>:
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-900 select-all">
                {resetResult.password}
              </code>
              <button
                onClick={handleCopyPassword}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-50 text-green-700'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              This password is shown only once. Make sure to copy it before closing.
            </p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setResetResult(null)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Organizations Tab ---

const ACCESS_DAYS_OPTIONS = [5, 10, 15, 30];

function OrganizationsTab({ organizations, loading }) {
  const [expandedId, setExpandedId] = useState(null);
  const [requestModalOrg, setRequestModalOrg] = useState(null); // org object
  const [requestDays, setRequestDays] = useState(5);
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]); // { [org_id]: request }
  const { showSuccess, showError } = useToast();

  // Load my existing access requests so we can show status per org
  useEffect(() => {
    apiRequest('/superadmin/my-access-requests').then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(r => {
          // Keep the latest request per org
          if (!map[r.organization_id] || new Date(r.created_at) > new Date(map[r.organization_id].created_at)) {
            map[r.organization_id] = r;
          }
        });
        setMyRequests(map);
      }
    }).catch(() => {});
  }, []);

  const openRequestModal = (e, org) => {
    e.stopPropagation();
    setRequestModalOrg(org);
    setRequestDays(5);
    setRequestMessage('');
  };

  const closeRequestModal = () => {
    if (submitting) return;
    setRequestModalOrg(null);
  };

  const handleSubmitRequest = async () => {
    if (!requestModalOrg) return;
    setSubmitting(true);
    try {
      const res = await apiRequest(`/superadmin/organizations/${requestModalOrg.id}/request-access`, {
        method: 'POST',
        body: JSON.stringify({
          organization_id: requestModalOrg.id,
          requested_days: requestDays,
          message: requestMessage.trim() || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        showSuccess(`Access request sent to owner of ${requestModalOrg.name}`);
        setMyRequests(prev => ({ ...prev, [requestModalOrg.id]: data }));
        setRequestModalOrg(null);
      } else {
        const err = await res.json();
        showError(err.detail || 'Failed to send access request');
      }
    } catch (e) {
      showError('Failed to send access request');
    } finally {
      setSubmitting(false);
    }
  };

  // Dismiss modal on Escape
  useEffect(() => {
    if (!requestModalOrg) return;
    const handler = (e) => { if (e.key === 'Escape') closeRequestModal(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [requestModalOrg, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRequestStatus = (orgId) => myRequests[orgId] || null;

  return (
    <div className="space-y-0">
      <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="w-8 px-2 py-2.5" />
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Brand</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Owner</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Members</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Forms</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Pages</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Sites</th>
              <th className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Prospecting</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Created</th>
              <th className="w-10 px-2 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {loading ? <LoadingRows cols={11} rows={5} /> : organizations.map(org => {
              const req = getRequestStatus(org.id);
              const isPending = req?.status === 'pending';
              const isApproved = req?.status === 'approved';
              return (
                <React.Fragment key={org.id}>
                  <tr
                    className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors"
                    onClick={() => setExpandedId(expandedId === org.id ? null : org.id)}
                  >
                    <td className="px-2 py-2.5 text-gray-300 pl-3">
                      {expandedId === org.id
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-900 font-medium">{org.name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md ${
                        org.brand === 'able' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {org.brand}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{org.owner_name || org.owner_email || '-'}</td>
                    <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">{org.member_count}</td>
                    <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">{org.form_count}</td>
                    <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">{org.page_count}</td>
                    <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">{org.site_count}</td>
                    <td className="px-3 py-2.5 text-center text-gray-500 tabular-nums">{org.prospecting_list_count}</td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs tabular-nums">{formatDate(org.created_at)}</td>
                    <td className="px-2 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      {isPending ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-md" title="Access request pending">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      ) : isApproved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-md" title={`Access until ${req?.access_expires_at ? new Date(req.access_expires_at).toLocaleDateString() : ''}`}>
                          <Check className="w-3 h-3" /> Access
                        </span>
                      ) : (
                        <button
                          onClick={(e) => openRequestModal(e, org)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                          title="Request temporary access"
                        >
                          <LogIn className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === org.id && (
                    <tr>
                      <td colSpan={11} className="bg-gray-50/50 px-4 py-3 pl-12">
                        <div className="flex gap-8 text-xs text-gray-500">
                          <div><span className="text-gray-400">Slug</span> <span className="text-gray-700 font-medium ml-1">{org.slug}</span></div>
                          <div><span className="text-gray-400">Owner</span> <span className="text-gray-700 ml-1">{org.owner_email || '-'}</span></div>
                          {req && (
                            <div>
                              <span className="text-gray-400">Access request</span>
                              <span className={`ml-1 font-medium ${
                                req.status === 'pending' ? 'text-amber-600' :
                                req.status === 'approved' ? 'text-green-600' :
                                req.status === 'denied' ? 'text-red-500' : 'text-gray-500'
                              }`}>
                                {req.status}
                                {req.status === 'approved' && req.access_expires_at && ` (until ${new Date(req.access_expires_at).toLocaleDateString()})`}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {!loading && organizations.length === 0 && (
          <div className="py-12 text-center text-[13px] text-gray-300">No organizations found</div>
        )}
      </div>

      {/* Request Access Modal */}
      {requestModalOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={closeRequestModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <LogIn className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Request access</h3>
                <p className="text-xs text-gray-400">{requestModalOrg.name}</p>
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-3 mb-4">
              The organisation owner will receive an email to approve or deny this request.
            </p>

            {/* Days selection */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">Access duration</label>
              <div className="flex gap-2">
                {ACCESS_DAYS_OPTIONS.map(d => (
                  <button
                    key={d}
                    onClick={() => setRequestDays(d)}
                    className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      requestDays === d
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {/* Optional message */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Message <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Describe why you need access..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-colors resize-none"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={closeRequestModal}
                disabled={submitting}
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Chat Tab ---

function formatTokens(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function ChatTab({ chatUsage, loading, onToggleCooldown, onUpdateTokenLimit }) {
  const [confirmCooldown, setConfirmCooldown] = useState(null); // { userId, email }
  const [editingLimit, setEditingLimit] = useState(null); // { userId, value }
  if (loading) return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="border border-gray-100 rounded-xl bg-white p-5 h-24 animate-pulse" />
      ))}
    </div>
  );
  if (!chatUsage) return null;

  return (
    <div className="space-y-6">
      {/* Top-level metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-gray-200/60 rounded-xl overflow-hidden border border-gray-200/60">
        {[
          { label: 'Total Chats', value: chatUsage.total_chats },
          { label: 'Total Messages', value: chatUsage.total_messages },
          { label: 'Input Tokens', value: formatTokens(chatUsage.total_input_tokens) },
          { label: 'Output Tokens', value: formatTokens(chatUsage.total_output_tokens) },
          { label: 'Total Tokens', value: formatTokens(chatUsage.total_tokens) },
        ].map(m => (
          <div key={m.label} className="bg-white p-5">
            <div className="text-2xl font-semibold text-gray-900 tracking-tight font-['Satoshi']">{m.value}</div>
            <div className="text-[13px] text-gray-400 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Growth row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200/60 rounded-xl overflow-hidden border border-gray-200/60">
        {[
          { label: 'Chats (7d)', value: chatUsage.chats_last_7_days },
          { label: 'Chats (30d)', value: chatUsage.chats_last_30_days },
          { label: 'Messages (7d)', value: chatUsage.messages_last_7_days },
          { label: 'Messages (30d)', value: chatUsage.messages_last_30_days },
        ].map(m => (
          <div key={m.label} className="bg-white p-5">
            <div className="text-xl font-semibold text-gray-900 tracking-tight font-['Satoshi']">{m.value}</div>
            <div className="text-[13px] text-gray-400 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By Model */}
        {chatUsage.by_model.length > 0 && (
          <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <span className="text-[13px] font-medium text-gray-900">Token Usage by Model</span>
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Model</th>
                  <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Messages</th>
                  <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Input</th>
                  <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Output</th>
                  <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {chatUsage.by_model.map(m => (
                  <tr key={m.model} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-gray-900">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700">{m.model}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">{m.message_count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">{formatTokens(m.input_tokens)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">{formatTokens(m.output_tokens)}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900 tabular-nums">{formatTokens(m.input_tokens + m.output_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent Chats */}
        <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-[13px] font-medium text-gray-900">Recent Chats</span>
          </div>
          <div className="divide-y divide-gray-50">
            {chatUsage.recent_chats.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-gray-300">No chats yet</div>
            ) : chatUsage.recent_chats.slice(0, 10).map(chat => (
              <div key={chat.id} className="px-4 py-2.5 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-gray-900 truncate">{chat.title}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-400">{chat.user_email}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500">{chat.model}</span>
                    <span className="text-xs text-gray-300">{chat.message_count} msgs</span>
                  </div>
                </div>
                <span className="text-xs text-gray-300 whitespace-nowrap ml-3 tabular-nums">{formatRelative(chat.updated_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* By User */}
      {chatUsage.by_user.length > 0 && (
        <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-[13px] font-medium text-gray-900">Usage by User</span>
          </div>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">User</th>
                <th className="text-center px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Status</th>
                <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Weekly Usage</th>
                <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Limit</th>
                <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Total Tokens</th>
                <th className="text-right px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {chatUsage.by_user.map(u => {
                const weeklyPct = u.weekly_token_limit > 0 ? Math.min((u.weekly_token_usage / u.weekly_token_limit) * 100, 100) : 0;
                const barColor = weeklyPct >= 100 ? 'bg-red-500' : weeklyPct >= 75 ? 'bg-amber-500' : 'bg-brand-500';
                return (
                  <tr key={u.user_email} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{u.user_name || u.user_email}</div>
                      {u.user_name && <div className="text-xs text-gray-400">{u.user_email}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md ${
                        u.chat_cooldown ? 'bg-red-50 text-red-700' : 'bg-brand-50 text-brand-700'
                      }`}>
                        {u.chat_cooldown ? <><Pause className="w-3 h-3" /> Cooldown</> : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${weeklyPct}%` }} />
                        </div>
                        <span className="text-gray-500 tabular-nums">{formatTokens(u.weekly_token_usage)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {editingLimit?.userId === u.user_id ? (
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          onUpdateTokenLimit(u.user_id, parseInt(editingLimit.value) || 2_000_000);
                          setEditingLimit(null);
                        }} className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            value={editingLimit.value}
                            onChange={(e) => setEditingLimit(prev => ({ ...prev, value: e.target.value }))}
                            className="w-24 px-2 py-0.5 text-xs border rounded text-right"
                            autoFocus
                          />
                          <button type="submit" className="text-brand-600 text-xs">Save</button>
                          <button type="button" onClick={() => setEditingLimit(null)} className="text-gray-400 text-xs">
                            <X className="w-3 h-3" />
                          </button>
                        </form>
                      ) : (
                        <button
                          onClick={() => setEditingLimit({ userId: u.user_id, value: String(u.weekly_token_limit) })}
                          className="text-gray-500 tabular-nums hover:text-gray-700 hover:underline"
                          title="Click to edit limit"
                        >
                          {formatTokens(u.weekly_token_limit)}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900 tabular-nums">{formatTokens(u.input_tokens + u.output_tokens)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => {
                          if (u.chat_cooldown) {
                            onToggleCooldown(u.user_id, false);
                          } else {
                            setConfirmCooldown({ userId: u.user_id, email: u.user_email });
                          }
                        }}
                        className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                          u.chat_cooldown
                            ? 'text-brand-600 hover:bg-brand-50'
                            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                      >
                        {u.chat_cooldown ? 'Lift Cooldown' : 'Cooldown'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cooldown Confirmation Modal */}
      {confirmCooldown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900">Enable Chat Cooldown</h3>
            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to restrict <strong>{confirmCooldown.email}</strong>'s chat access?
              They will not be able to use chat until you manually lift the cooldown.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmCooldown(null)}
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onToggleCooldown(confirmCooldown.userId, true);
                  setConfirmCooldown(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Enable Cooldown
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Usage Tab ---

function UsageTab({ usage, loading }) {
  if (loading) return (
    <div className="grid grid-cols-2 gap-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="border border-gray-100 rounded-xl bg-white p-5 h-36 animate-pulse" />
      ))}
    </div>
  );
  if (!usage) return null;

  return (
    <div className="space-y-5">
      {/* Status breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="border border-gray-100 rounded-xl bg-white p-5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-4">Forms by Status</h3>
          <div className="flex gap-3">
            <MetricBlock label="Draft" value={usage.forms_draft} />
            <MetricBlock label="Published" value={usage.forms_published} accent />
          </div>
        </div>
        <div className="border border-gray-100 rounded-xl bg-white p-5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-4">Sites by Deployment</h3>
          <div className="flex gap-3">
            <MetricBlock label="Deployed" value={usage.sites_deployed} accent />
            <MetricBlock label="Not Deployed" value={usage.sites_not_deployed} />
          </div>
        </div>
      </div>

      {/* Deals */}
      {Object.keys(usage.deals_by_stage).length > 0 && (
        <div className="border border-gray-100 rounded-xl bg-white p-5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-4">Deals by Stage</h3>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(usage.deals_by_stage).map(([state, count]) => (
              <MetricBlock key={state} label={state} value={count} />
            ))}
          </div>
        </div>
      )}

      {/* Growth */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <GrowthPanel title="Last 7 Days" data={usage.created_last_7_days} />
        <GrowthPanel title="Last 30 Days" data={usage.created_last_30_days} />
      </div>
    </div>
  );
}

function MetricBlock({ label, value, accent }) {
  return (
    <div className={`flex-1 rounded-lg px-4 py-3 ${accent ? 'bg-brand-50' : 'bg-gray-50'}`}>
      <div className={`text-xl font-semibold tracking-tight font-['Satoshi'] ${accent ? 'text-brand-700' : 'text-gray-900'}`}>
        {value}
      </div>
      <div className={`text-xs mt-0.5 capitalize ${accent ? 'text-brand-600/70' : 'text-gray-400'}`}>{label}</div>
    </div>
  );
}

function GrowthPanel({ title, data }) {
  return (
    <div className="border border-gray-100 rounded-xl bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <ArrowUpRight className="w-3.5 h-3.5 text-brand-500" strokeWidth={2} />
        <h3 className="text-xs font-medium uppercase tracking-wider text-gray-400">New &mdash; {title}</h3>
      </div>
      <div className="space-y-2">
        {Object.entries(data || {}).map(([key, val]) => (
          <div key={key} className="flex items-center justify-between py-1.5">
            <span className="text-[13px] text-gray-500 capitalize">{key}</span>
            <span className="text-[13px] font-semibold text-gray-900 tabular-nums">{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Prospecting Tab ---

function ProgressBar({ used, cap }) {
  if (cap == null || cap <= 0) {
    return <span className="text-xs text-gray-400 tabular-nums">{used}</span>;
  }
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const tone =
    pct >= 100 ? 'bg-red-500' :
    pct >= 80 ? 'bg-amber-500' :
    'bg-gray-400';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <span className="text-xs text-gray-700 tabular-nums whitespace-nowrap">
        {used}<span className="text-gray-300"> / {cap}</span>
      </span>
      <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    completed: 'bg-gray-100 text-gray-600',
    running: 'bg-amber-50 text-amber-700',
    queued: 'bg-blue-50 text-blue-700',
    failed: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[11px] font-medium rounded ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  );
}

function ProspectingTab({ data, loading }) {
  if (loading) return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 rounded-xl overflow-hidden border border-gray-100">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white p-5">
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse mb-2" />
            <div className="h-3 w-20 bg-gray-50 rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="border border-gray-100 rounded-xl bg-white p-5 h-48 animate-pulse" />
    </div>
  );
  if (!data) return null;

  const failedJobs = data.jobs_by_status?.failed || 0;
  const runningJobs = data.jobs_by_status?.running || 0;

  const topMetrics = [
    { label: 'Active Lists', value: data.total_lists_active, icon: ClipboardList },
    { label: 'Archived', value: data.total_lists_archived, icon: Archive },
    { label: 'Records', value: data.total_records, icon: FileText },
    { label: 'Enrichment Jobs', value: data.total_enrichment_jobs, icon: Zap },
    { label: 'Rows Enriched', value: data.total_enriched_rows, icon: Target },
    { label: 'Failed Jobs', value: failedJobs, icon: AlertTriangle, accent: failedJobs > 0 ? 'red' : null },
    { label: 'Live Lookups Today', value: data.live_lookups_today_total, icon: Search },
    { label: 'Live Lookups 30d', value: data.live_lookups_30d_total, icon: Search },
  ];

  return (
    <div className="space-y-6">
      {/* Top stat grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200/60 rounded-xl overflow-hidden border border-gray-200/60">
        {topMetrics.map(({ label, value, icon: Icon, accent }) => (
          <div key={label} className="bg-white p-5">
            <div className={`text-2xl font-semibold tracking-tight font-['Satoshi'] ${
              accent === 'red' ? 'text-red-600' : 'text-gray-900'
            }`}>
              {(value ?? 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <Icon className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.5} />
              <span className="text-[13px] text-gray-400">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {!data.live_lookup_data_available && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-[13px] text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
          Lead data service is unavailable. Live-lookup metrics are hidden.
        </div>
      )}

      {/* Growth */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <GrowthPanel title="Last 7 Days" data={data.created_last_7_days} />
        <GrowthPanel title="Last 30 Days" data={data.created_last_30_days} />
      </div>

      {/* Enrichment job health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="border border-gray-100 rounded-xl bg-white">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-[13px] font-medium text-gray-900">Jobs by Status</span>
          </div>
          {Object.keys(data.jobs_by_status || {}).length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-gray-300">No enrichment jobs yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {Object.entries(data.jobs_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between px-4 py-2.5">
                  <StatusBadge status={status} />
                  <span className="text-[13px] font-medium text-gray-700 tabular-nums">{count}</span>
                </div>
              ))}
              {runningJobs > 0 && (
                <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50/50 flex items-center gap-1.5">
                  <Zap className="w-3 h-3" strokeWidth={2} />
                  {runningJobs} job{runningJobs === 1 ? '' : 's'} running
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border border-gray-100 rounded-xl bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-[13px] font-medium text-gray-900">Recent Enrichment Jobs</span>
            <span className="text-xs text-gray-300 tabular-nums">{data.recent_enrichment_jobs.length}</span>
          </div>
          <div className="divide-y divide-gray-50 max-h-96 overflow-auto">
            {data.recent_enrichment_jobs.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-gray-300">No enrichment jobs yet</div>
            ) : data.recent_enrichment_jobs.map(job => {
              const total = job.rows_total || 0;
              const done = job.rows_done || 0;
              const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
              return (
                <div key={job.id} className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-gray-900 truncate">
                        {job.list_name || 'Unknown list'}
                        {job.field_label && <span className="text-gray-400 font-normal"> · {job.field_label}</span>}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{job.organization_name || '—'}</div>
                    </div>
                    <StatusBadge status={job.status} />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${job.status === 'failed' ? 'bg-red-400' : 'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                      {done}/{total}
                      {job.rows_failed > 0 && <span className="text-red-500"> · {job.rows_failed} failed</span>}
                    </span>
                    <span className="text-[11px] text-gray-300 whitespace-nowrap">{formatRelative(job.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per-org table */}
      <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-[13px] font-medium text-gray-900">Usage by Organization</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Organization</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Saved Lists</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Archived</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Records</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Jobs</th>
                <th className="text-right px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Rows Enriched</th>
                {data.live_lookup_data_available && (
                  <>
                    <th className="text-left px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Live Today</th>
                    <th className="text-right px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Live 30d</th>
                  </>
                )}
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {data.by_org.length === 0 ? (
                <tr><td colSpan={data.live_lookup_data_available ? 9 : 7} className="px-4 py-8 text-center text-[13px] text-gray-300">No prospecting activity yet</td></tr>
              ) : data.by_org.map(org => (
                <tr key={org.organization_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 text-gray-900 font-medium">{org.organization_name}</td>
                  <td className="px-3 py-2.5"><ProgressBar used={org.list_count} cap={org.list_cap} /></td>
                  <td className="px-3 py-2.5 text-right text-gray-400 tabular-nums">{org.archived_list_count}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">{org.record_count.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">{org.enrichment_job_count}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">{org.enriched_rows.toLocaleString()}</td>
                  {data.live_lookup_data_available && (
                    <>
                      <td className="px-3 py-2.5"><ProgressBar used={org.live_lookups_today} cap={org.live_lookup_daily_limit} /></td>
                      <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">{org.live_lookups_30d.toLocaleString()}</td>
                    </>
                  )}
                  <td className="px-4 py-2.5 text-gray-400 text-xs tabular-nums whitespace-nowrap">{formatRelative(org.last_activity_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-user + Recent Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="border border-gray-100 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-[13px] font-medium text-gray-900">Top Users</span>
          </div>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">User</th>
                <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Lists</th>
                <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Jobs</th>
                <th className="text-right px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Rows</th>
                <th className="text-left px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-400">Last List</th>
              </tr>
            </thead>
            <tbody>
              {data.by_user.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[13px] text-gray-300">No users have created lists yet</td></tr>
              ) : data.by_user.map(u => (
                <tr key={u.user_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-2 min-w-0">
                    <div className="text-[13px] text-gray-900 font-medium truncate">{u.full_name || u.email}</div>
                    {u.full_name && <div className="text-xs text-gray-400 truncate">{u.email}</div>}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{u.list_count}</td>
                  <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{u.enrichment_job_count}</td>
                  <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{u.enriched_rows.toLocaleString()}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs tabular-nums whitespace-nowrap">{formatRelative(u.last_list_created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ActivityPanel
          title="Recent Lists"
          items={data.recent_lists}
          renderItem={(item) => (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-medium text-gray-900 truncate">{item.name}</span>
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    item.entity_type === 'company' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                  }`}>{item.entity_type}</span>
                  {item.archived_at && <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500">archived</span>}
                </div>
                <span className="text-xs text-gray-400 truncate block">
                  {item.record_count} records · {item.organization_name || '—'}
                  {item.created_by_email && <> · {item.created_by_email}</>}
                </span>
              </div>
              <span className="text-xs text-gray-300 whitespace-nowrap tabular-nums">{formatRelative(item.created_at)}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
}

// --- Main Dashboard ---

export default function SuperAdminDashboard() {
  useAuth();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activity, setActivity] = useState(null);
  const [usage, setUsage] = useState(null);
  const [chatUsage, setChatUsage] = useState(null);
  const [prospectingUsage, setProspectingUsage] = useState(null);

  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingChatUsage, setLoadingChatUsage] = useState(true);
  const [loadingProspectingUsage, setLoadingProspectingUsage] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/stats');
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Failed to load stats:', e); }
    setLoadingStats(false);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/users');
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error('Failed to load users:', e); }
    setLoadingUsers(false);
  }, []);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/organizations');
      if (res.ok) setOrganizations(await res.json());
    } catch (e) { console.error('Failed to load organizations:', e); }
    setLoadingOrgs(false);
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/activity');
      if (res.ok) setActivity(await res.json());
    } catch (e) { console.error('Failed to load activity:', e); }
    setLoadingActivity(false);
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/usage');
      if (res.ok) setUsage(await res.json());
    } catch (e) { console.error('Failed to load usage:', e); }
    setLoadingUsage(false);
  }, []);

  const fetchChatUsage = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/chat-usage');
      if (res.ok) setChatUsage(await res.json());
    } catch (e) { console.error('Failed to load chat usage:', e); }
    setLoadingChatUsage(false);
  }, []);

  const fetchProspectingUsage = useCallback(async () => {
    try {
      const res = await apiRequest('/superadmin/prospecting-usage');
      if (res.ok) setProspectingUsage(await res.json());
    } catch (e) { console.error('Failed to load prospecting usage:', e); }
    setLoadingProspectingUsage(false);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchActivity();
  }, [fetchStats, fetchActivity]);

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0 && loadingUsers) fetchUsers();
    if (activeTab === 'organizations' && organizations.length === 0 && loadingOrgs) fetchOrgs();
    if (activeTab === 'chat' && !chatUsage && loadingChatUsage) fetchChatUsage();
    if (activeTab === 'usage' && !usage && loadingUsage) fetchUsage();
    if (activeTab === 'prospecting' && !prospectingUsage && loadingProspectingUsage) fetchProspectingUsage();
  }, [activeTab, users.length, organizations.length, usage, chatUsage, prospectingUsage, loadingUsers, loadingOrgs, loadingUsage, loadingChatUsage, loadingProspectingUsage, fetchUsers, fetchOrgs, fetchUsage, fetchChatUsage, fetchProspectingUsage]);

  const handleToggleCooldown = async (userId, enabled) => {
    try {
      const res = await apiRequest(`/superadmin/users/${userId}/chat-cooldown`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        const data = await res.json();
        // Update both users and chatUsage state
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, chat_cooldown: data.chat_cooldown } : u));
        setChatUsage(prev => prev ? {
          ...prev,
          by_user: prev.by_user.map(u => u.user_id === userId ? { ...u, chat_cooldown: data.chat_cooldown, cooldown_set_at: data.chat_cooldown_set_at } : u),
        } : prev);
      }
    } catch (e) {
      console.error('Failed to toggle cooldown:', e);
    }
  };

  const handleUpdateTokenLimit = async (userId, newLimit) => {
    try {
      const res = await apiRequest(`/superadmin/users/${userId}/chat-token-limit`, {
        method: 'PUT',
        body: JSON.stringify({ weekly_limit: newLimit }),
      });
      if (res.ok) {
        // Refresh chat usage to get updated limits
        fetchChatUsage();
      }
    } catch (e) {
      console.error('Failed to update token limit:', e);
    }
  };

  const handleToggleSuperadmin = async (userId, newValue) => {
    try {
      const res = await apiRequest(`/superadmin/users/${userId}/superadmin`, {
        method: 'PUT',
        body: JSON.stringify({ is_superadmin: newValue }),
      });
      if (res.ok) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_superadmin: newValue } : u));
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update user');
      }
    } catch (e) {
      console.error('Failed to toggle superadmin:', e);
      alert('Failed to update user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await apiRequest(`/superadmin/users/${userId}/reset-password`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        showSuccess(`Password reset for ${data.email}`);
        return data;
      } else {
        const err = await res.json();
        showError(err.detail || 'Failed to reset password');
        return null;
      }
    } catch (e) {
      console.error('Failed to reset password:', e);
      showError('Failed to reset password');
      return null;
    }
  };

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight font-['Satoshi']">Admin</h1>
          <p className="text-sm text-gray-400 mt-0.5">Platform overview and management</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100 -mx-1">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-[13px] font-medium rounded-t-lg transition-colors relative ${
                isActive
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
              {isActive && (
                <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-gray-900 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <OverviewTab stats={stats} activity={activity} loadingStats={loadingStats} loadingActivity={loadingActivity} />
      )}
      {activeTab === 'users' && (
        <UsersTab users={users} loading={loadingUsers} onToggleSuperadmin={handleToggleSuperadmin} onToggleCooldown={handleToggleCooldown} onResetPassword={handleResetPassword} />
      )}
      {activeTab === 'organizations' && (
        <OrganizationsTab organizations={organizations} loading={loadingOrgs} />
      )}
      {activeTab === 'chat' && (
        <ChatTab chatUsage={chatUsage} loading={loadingChatUsage} onToggleCooldown={handleToggleCooldown} onUpdateTokenLimit={handleUpdateTokenLimit} />
      )}
      {activeTab === 'usage' && (
        <UsageTab usage={usage} loading={loadingUsage} />
      )}
      {activeTab === 'prospecting' && (
        <ProspectingTab data={prospectingUsage} loading={loadingProspectingUsage} />
      )}
    </div>
  );
}
